import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  Unsubscribe,
  updateDoc,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import {
  ConnectorDefinition,
  ConnectorInstallation,
  ConnectorPolicy,
  ConnectorToolDefinition,
} from '@superfunnel/types/connector';
import { WithId } from '@superfunnel/types/dynamic-page-version';

import { auth, db, functions } from '../Firebase';
import { emptyConverter } from '../utils';

interface InstallConnectorInput {
  accountId: string;
  connectorDefinitionId?: string;
  customRemoteUrl?: string;
  displayName?: string;
  availableAgentIds?: string[];
  availableUserIds?: string[];
  apiKey?: string;
  apiKeyHeaderName?: string;
}

interface StartConnectorOAuthInput {
  accountId: string;
  connectorDefinitionId?: string;
  customRemoteUrl?: string;
  connectBoth?: boolean;
  installationId?: string;
  displayName?: string;
  availableAgentIds?: string[];
  availableUserIds?: string[];
  redirectPath?: string;
}

interface StartConnectorOAuthResult {
  authorizationUrl: string;
  requestId: string;
  provider: string;
}

interface UpdateConnectorInstallationInput {
  accountId: string;
  installationId: string;
  displayName?: string;
  enabled?: boolean;
  availableAgentIds?: string[];
  availableUserIds?: string[];
  configuration?: ConnectorInstallation['configuration'];
}

interface SetConnectorStaticSecretInput {
  accountId: string;
  installationId: string;
  apiKey: string;
  ownerScope?: 'account' | 'user';
}

interface UpdateCustomConnectorInput {
  accountId: string;
  installationId: string;
  customRemoteUrl: string;
  displayName?: string;
  authSource: 'none' | 'static_secret';
  apiKey?: string;
  apiKeyHeaderName?: string;
}

interface UninstallConnectorInput {
  accountId: string;
  installationId: string;
}

interface UninstallConnectorResult {
  success: true;
  installationId: string;
}

interface RefreshConnectorCapabilitiesInput {
  accountId: string;
  installationId: string;
  generateEmbeddings?: boolean;
}

interface RefreshConnectorCapabilitiesResult {
  success: true;
  installationId: string;
  snapshotVersion: string;
  toolCount: number;
  resourceCount: number;
  templateCount: number;
  embeddingFailures: number;
}

interface TestConnectorConnectionInput {
  accountId: string;
  installationId: string;
}

interface TestConnectorConnectionResult {
  success: true;
  installationId: string;
  toolCount: number;
}

const callConnectorFunction = async <Request, Response>(
  functionName: string,
  data: Request,
): Promise<Response> => {
  const fn = httpsCallable<Request, Response>(functions, functionName);
  const res = await fn(data);

  return res.data;
};

const toWithId = <T>(id: string, data: T): WithId<T> => ({
  ...data,
  id,
});

const getCurrentUserId = (): string => {
  const userId = auth.currentUser?.uid;

  if (!userId) {
    throw new Error('You must be signed in to update connectors');
  }

  return userId;
};

export const connectorDefinitionsColRef = () =>
  collection(db, 'connectorDefinitions').withConverter(
    emptyConverter<ConnectorDefinition>(),
  );

export const connectorDefinitionToolsColRef = (connectorDefinitionId: string) =>
  collection(
    db,
    'connectorDefinitions',
    connectorDefinitionId,
    'tools',
  ).withConverter(emptyConverter<ConnectorToolDefinition>());

export const connectorInstallationsColRef = (accountId: string) =>
  collection(db, 'accounts', accountId, 'connectorInstallations').withConverter(
    emptyConverter<ConnectorInstallation>(),
  );

const connectorInstallationDocRef = (
  accountId: string,
  installationId: string,
) => doc(connectorInstallationsColRef(accountId), installationId);

export const listConnectorDefinitions = async (
  accountId: string,
  options?: { includeInactive?: boolean; isSupportAdmin?: boolean },
): Promise<WithId<ConnectorDefinition>[]> => {
  void accountId;

  const { includeInactive, isSupportAdmin } = options ?? {};

  const lifecycleStatuses = ['active'];
  if (isSupportAdmin) {
    lifecycleStatuses.push('beta');
  }
  if (isSupportAdmin || import.meta.env.DEV) {
    lifecycleStatuses.push('internal');
  }

  const definitions = await getDocs(
    includeInactive
      ? connectorDefinitionsColRef()
      : query(
          connectorDefinitionsColRef(),
          where('lifecycleStatus', 'in', lifecycleStatuses),
        ),
  );

  return definitions.docs.map((doc) => toWithId(doc.id, doc.data()));
};

export const listConnectorDefinitionTools = async (
  connectorDefinitionId: string,
): Promise<WithId<ConnectorToolDefinition>[]> => {
  const tools = await getDocs(
    connectorDefinitionToolsColRef(connectorDefinitionId),
  );

  return tools.docs.map((doc) => toWithId(doc.id, doc.data()));
};

export const listConnectorInstallations = async (
  accountId: string,
  includeDeleted?: boolean,
): Promise<WithId<ConnectorInstallation>[]> => {
  const installations = await getDocs(connectorInstallationsColRef(accountId));

  return installations.docs
    .map((doc) => toWithId(doc.id, doc.data()))
    .filter(
      (installation) => includeDeleted || installation.status !== 'deleted',
    );
};

export const subscribeToConnectorInstallations = (
  accountId: string,
  onChange: (installations: WithId<ConnectorInstallation>[]) => void,
  onError: (error: Error) => void,
): Unsubscribe =>
  onSnapshot(
    connectorInstallationsColRef(accountId),
    (snapshot) =>
      onChange(
        snapshot.docs
          .map((doc) => toWithId(doc.id, doc.data()))
          .filter((installation) => installation.status !== 'deleted'),
      ),
    onError,
  );

export const installConnector = async (
  data: InstallConnectorInput,
): Promise<WithId<ConnectorInstallation>> => {
  return callConnectorFunction('connectors/install', data);
};

export const startConnectorOAuth = async (
  data: StartConnectorOAuthInput,
): Promise<StartConnectorOAuthResult> => {
  return callConnectorFunction('connectors/startOauth', data);
};

export const updateConnectorInstallation = async (
  data: UpdateConnectorInstallationInput,
): Promise<void> => {
  const update: Record<string, unknown> = {
    updatedAt: serverTimestamp(),
    updatedBy: getCurrentUserId(),
  };

  if (data.displayName !== undefined) {
    update.displayName = data.displayName;
  }

  if (data.availableAgentIds !== undefined) {
    update.availableAgentIds = data.availableAgentIds;
  }

  if (data.availableUserIds !== undefined) {
    update.availableUserIds = data.availableUserIds;
  }

  if (data.configuration !== undefined) {
    update.configuration = data.configuration;
  }

  if (data.enabled !== undefined) {
    update.enabled = data.enabled;
    update.status = data.enabled ? 'active' : 'disabled';
  }

  await updateDoc(
    connectorInstallationDocRef(data.accountId, data.installationId),
    update,
  );
};

export const setConnectorStaticSecret = async (
  data: SetConnectorStaticSecretInput,
): Promise<WithId<ConnectorInstallation>> => {
  return callConnectorFunction('connectors/setStaticSecret', data);
};

export const updateCustomConnector = async (
  data: UpdateCustomConnectorInput,
): Promise<WithId<ConnectorInstallation>> => {
  return callConnectorFunction('connectors/updateCustom', data);
};

export const uninstallConnector = async (
  data: UninstallConnectorInput,
): Promise<UninstallConnectorResult> => {
  return callConnectorFunction('connectors/uninstall', data);
};

export const updateConnectorPolicy = async (
  accountId: string,
  installationId: string,
  policy: ConnectorPolicy,
): Promise<void> => {
  const update: Record<string, unknown> = {
    policy,
    updatedAt: serverTimestamp(),
    updatedBy: getCurrentUserId(),
  };

  await updateDoc(
    connectorInstallationDocRef(accountId, installationId),
    update,
  );
};

export const refreshConnectorCapabilities = async (
  data: RefreshConnectorCapabilitiesInput,
): Promise<RefreshConnectorCapabilitiesResult> => {
  return callConnectorFunction('connectors/refreshCapabilities', data);
};

export const testConnectorConnection = async (
  data: TestConnectorConnectionInput,
): Promise<TestConnectorConnectionResult> => {
  return callConnectorFunction('connectors/testConnection', data);
};
